import React, { useState, useEffect } from 'react';
import { Inbox, X, CheckCircle2, Link2, RefreshCw } from 'lucide-react';
import { FaxRecord, Client } from '../types';
import { markFaxReviewed, markAllIncomingFaxesReviewed, syncIncomingFaxes } from '../utils/incomingFaxState';

export function incomingFaxAttachRequestBody(input: {
  clientId: string;
  docType: string;
  pocEndDate?: string;
  notes?: string;
}) {
  return {
    client_id: input.clientId,
    doc_type: input.docType,
    poc_end_date: input.pocEndDate || undefined,
    notes: input.notes || 'Attached incoming medical records to chart'
  };
}

interface IncomingFaxesModalProps {
  onClose: () => void;
  onRefreshStats?: () => void;
  userRole?: string;
  onViewClientChart?: (clientId: string) => void;
}

export const IncomingFaxesModal: React.FC<IncomingFaxesModalProps> = ({
  onClose,
  onRefreshStats,
  userRole,
  onViewClientChart
}) => {
  const [faxes, setFaxes] = useState<FaxRecord[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedFax, setSelectedFax] = useState<FaxRecord | null>(null);
  const [targetClientId, setTargetClientId] = useState('');
  const [attachNotes, setAttachNotes] = useState('');
  const [attachSuccess, setAttachSuccess] = useState(false);
  const [attachError, setAttachError] = useState('');
  const [docType, setDocType] = useState('EXTERNAL');
  const [pocEndDate, setPocEndDate] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [faxRes, cliRes] = await Promise.all([
        fetch('/api/communications/faxes'),
        fetch('/api/clients')
      ]);
      if (faxRes.ok) {
        const data = await faxRes.json();
        setFaxes(data);
        if (data.length > 0 && !selectedFax) {
          setSelectedFax(data[0]);
        }
        // Mark all incoming faxes reviewed as part of opening the canonical Fax Inbox
        markAllIncomingFaxesReviewed();
      }
      if (cliRes.ok) {
        setClients(await cliRes.json());
      }
    } catch (err) {
      console.error('Error fetching faxes:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (!targetClientId) {
      setPocEndDate('');
      return;
    }
    const selectedCli = clients.find(c => c.id === targetClientId);
    if (selectedCli && selectedCli.poc_end_date) {
      setPocEndDate(selectedCli.poc_end_date);
    }
  }, [targetClientId, clients]);

  const handleAttachFax = async () => {
    if (!selectedFax || !targetClientId) return;

    try {
      setAttachError('');
      const res = await fetch(`/api/communications/faxes/${selectedFax.id}/attach`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(incomingFaxAttachRequestBody({
          clientId: targetClientId,
          docType,
          pocEndDate,
          notes: attachNotes
        }))
      });

      if (res.ok) {
        setAttachSuccess(true);
        markFaxReviewed(selectedFax.id);
        await fetchData();
        syncIncomingFaxes();
        setTimeout(() => setAttachSuccess(false), 3000);
      } else {
        const data = await res.json().catch(() => ({}));
        setAttachError(data.error || 'Could not file this fax.');
      }
    } catch (err) {
      console.error('Attach error:', err);
      setAttachError('Could not file this fax.');
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4">
      <div className="relative max-w-5xl w-full rounded-2xl border border-slate-300 bg-white shadow-xl overflow-hidden flex flex-col max-h-[88vh]">
        {/* Structural Header (Deep Slate-Blue / Navy) */}
        <div className="px-4 py-2.5 bg-gradient-to-r from-[#2a394d] via-[#1a2a42] to-[#0d1727] border-b border-slate-700/80 flex justify-between items-center shrink-0">
          <div className="flex items-center space-x-2.5">
            <div className="w-7 h-7 rounded-lg bg-white/10 border border-white/20 flex items-center justify-center text-slate-200 shrink-0">
              <Inbox className="w-3.5 h-3.5 text-slate-200" />
            </div>
            <div>
              <h2 className="text-xs sm:text-sm font-bold text-white tracking-tight leading-tight">Central Fax Inbox & Telephony</h2>
              <p className="text-[10px] text-slate-300 font-mono leading-none mt-0.5">Electronic facsimile queue • HIPAA verified direct transport</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => fetchData()}
              disabled={loading}
              className="px-2.5 py-1 text-xs font-medium rounded-lg bg-slate-800/80 hover:bg-slate-700 border border-slate-600 text-slate-200 hover:text-white shadow-xs flex items-center space-x-1.5 transition-all cursor-pointer disabled:opacity-50"
              title="Sync status with Faxage and reload queue"
            >
              <RefreshCw className={`w-3 h-3 text-slate-300 ${loading ? 'animate-spin' : ''}`} />
              <span>Sync & Refresh</span>
            </button>
            <button
              onClick={onClose}
              className="p-1 text-slate-300 hover:text-white rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
              title="Close fax inbox"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Grid: List on Left, Preview & Attach on Right */}
        <div className="grid grid-cols-1 md:grid-cols-12 flex-1 overflow-hidden">
          {/* Left Column: Faxes List */}
          <div className="md:col-span-5 border-r border-slate-200 flex flex-col max-h-[calc(88vh-50px)] overflow-y-auto bg-slate-50">
            <div className="px-3 py-1.5 bg-[#162030] border-b border-slate-700 text-[10px] uppercase font-bold text-slate-200 tracking-wider flex items-center justify-between shrink-0">
              <span>Inbox / Outbox Queue ({faxes.length})</span>
            </div>

            <div className="divide-y divide-slate-200">
              {loading ? (
                <div className="p-6 text-center text-xs text-slate-500 font-mono">Loading faxes...</div>
              ) : faxes.length === 0 ? (
                <div className="p-6 text-center text-xs text-slate-500 font-mono">No faxes currently in queue.</div>
              ) : (
                faxes.map((f) => {
                  const isSelected = selectedFax?.id === f.id;
                  const isIncoming = f.direction === 'INCOMING';
                  return (
                    <div
                      key={f.id}
                      onClick={() => {
                        setSelectedFax(f);
                        setTargetClientId(f.client_id || '');
                        markFaxReviewed(f.id);
                      }}
                      className={`px-3 py-2 cursor-pointer transition-colors duration-150 border-l-3 ${
                        isSelected
                          ? 'bg-slate-100 border-slate-800'
                          : 'bg-white hover:bg-slate-50/80 border-transparent'
                      }`}
                    >
                      {/* Row 1: Sender + Direction */}
                      <div className="flex justify-between items-center gap-1.5">
                        <span className="font-semibold text-xs text-slate-900 truncate">
                          {f.recipient_or_sender_name || f.recipient_or_sender_fax}
                        </span>
                        <span className={`text-[8.5px] font-mono px-1.5 py-0.2 rounded font-bold uppercase tracking-wider shrink-0 ${
                          isIncoming
                            ? 'bg-slate-200 text-slate-800 border border-slate-300'
                            : 'bg-slate-100 text-slate-600 border border-slate-200'
                        }`}>
                          {f.direction}
                        </span>
                      </div>

                      {/* Row 2: Document Title + Compact Timestamp */}
                      <div className="text-[10.5px] text-slate-600 mt-0.5 flex items-center justify-between gap-1.5">
                        <span className="truncate">{f.document_name}</span>
                        <span className="font-mono text-[9.5px] text-slate-500 shrink-0">{f.created_at?.slice(0, 10)}</span>
                      </div>

                      {/* Row 3: Status + Attached Client */}
                      <div className="mt-1 flex items-center justify-between text-[10px] gap-2">
                        <span className={`font-mono font-medium text-[9.5px] ${
                          f.status === 'DELIVERED' || f.status === 'RECEIVED' ? 'text-emerald-700' : 'text-amber-700'
                        }`}>
                          {f.status}
                        </span>
                        {f.first_name ? (
                          <span className="text-slate-700 text-[9.5px] truncate max-w-[180px]">
                            Attached: <strong className="font-medium text-slate-900">{f.first_name} {f.last_name}</strong>
                          </span>
                        ) : (
                          <span className="text-slate-400 text-[9.5px]">Unattached</span>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* Right Column: Preview & Attachment Panel */}
          <div className="md:col-span-7 p-3 sm:p-3.5 space-y-2.5 overflow-y-auto text-xs bg-slate-100/60 max-h-[calc(88vh-50px)]">
            {selectedFax ? (
              <div className="space-y-2.5">
                {/* Fax Details Header */}
                <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2 shadow-xs">
                  <div className="flex justify-between items-start gap-2">
                    <div className="min-w-0">
                      <h3 className="font-bold text-xs sm:text-sm text-slate-900 truncate">{selectedFax.document_name}</h3>
                      <div className="text-[10px] text-slate-500 font-mono mt-0.5 flex flex-wrap items-center gap-1.5">
                        <span>Fax ID: <span className="text-slate-700">{selectedFax.id}</span></span>
                        <span>•</span>
                        <span className={`text-[8.5px] font-mono px-1.5 py-0.2 rounded font-bold uppercase tracking-wider ${
                          selectedFax.direction === 'INCOMING'
                            ? 'bg-slate-200 text-slate-800 border border-slate-300'
                            : 'bg-slate-100 text-slate-600 border border-slate-200'
                        }`}>
                          {selectedFax.direction}
                        </span>
                        <span>•</span>
                        <span className="text-slate-500">{selectedFax.created_at}</span>
                      </div>
                    </div>
                    <span className={`text-[9.5px] font-mono px-2 py-0.5 rounded font-bold shrink-0 ${
                      selectedFax.status === 'DELIVERED' || selectedFax.status === 'RECEIVED'
                        ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
                        : 'bg-amber-50 text-amber-800 border border-amber-200'
                    }`}>
                      {selectedFax.status}
                    </span>
                  </div>

                  <div className="grid grid-cols-2 gap-2 text-[10.5px] pt-1.5 border-t border-slate-100">
                    <div>
                      <span className="text-slate-500 block text-[9.5px]">Sender / Recipient:</span>
                      <span className="text-slate-900 font-medium truncate block">{selectedFax.recipient_or_sender_name}</span>
                    </div>
                    <div>
                      <span className="text-slate-500 block text-[9.5px]">Fax Number:</span>
                      <span className="text-slate-900 font-mono text-[10px] truncate block">{selectedFax.recipient_or_sender_fax}</span>
                    </div>
                  </div>

                  {selectedFax.failure_reason && (
                    <div className="p-2 rounded bg-rose-50 border border-rose-200 text-rose-800 text-[10.5px]">
                      <span className="font-bold">Failure Notice: </span>
                      {selectedFax.failure_reason}
                    </div>
                  )}

                  {selectedFax.notes && (
                    <div className="text-slate-700 text-[10.5px] italic bg-slate-50 border border-slate-200 p-2 rounded">
                      "{selectedFax.notes}"
                    </div>
                  )}
                </div>

                {/* Document Preview Box */}
                <div className="border border-slate-200 rounded-xl overflow-hidden bg-white shadow-xs">
                  <div className="px-3 py-1.5 bg-[#162030] border-b border-slate-700 flex justify-between items-center text-[9.5px] uppercase font-bold text-slate-200">
                    <span>Document Content Preview (Page 1 of {selectedFax.pages || 1})</span>
                    <span className="font-mono text-emerald-400 flex items-center space-x-1.5">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 inline-block" />
                      <span>Electronic TIFF / PDF Stream</span>
                    </span>
                  </div>
                  <div className="p-4 sm:p-5 font-mono text-[11px] text-slate-800 space-y-2.5 leading-relaxed bg-white">
                    <div className="text-center font-bold text-slate-900 border-b border-slate-200 pb-2">
                      ALLEGIANT REHABILITATION SERVICES — MEDICAL RECORDS FACSIMILE
                    </div>
                    <div className="flex justify-between text-slate-500 text-[10px]">
                      <span>TRANSMISSION DATE: {selectedFax.created_at?.slice(0, 10)}</span>
                      <span>PAGES: {selectedFax.pages || 1}</span>
                    </div>
                    <p className="text-slate-800">
                      DOCUMENT: {selectedFax.document_name}
                    </p>
                    <p className="text-slate-600 text-xs">
                      [Clinical Record / Pediatric Speech Evaluation documentation transmitted via HIPAA-compliant digital facsimile channel.]
                    </p>
                    <div className="p-2.5 rounded bg-slate-50 border border-slate-200 text-slate-700 text-[11px]">
                      Status: {selectedFax.status}. This transmission contains protected health information (PHI) intended solely for the designated recipient.
                    </div>
                  </div>
                </div>

                {/* Attach to Client Chart Action */}
                <div className="rounded-xl border border-slate-200 bg-white p-3 space-y-2 shadow-xs">
                  <div className="flex items-center space-x-1.5 text-xs font-bold uppercase tracking-wider text-slate-800">
                    <Link2 className="w-3.5 h-3.5 text-slate-600" />
                    <span>Attach Fax to Client Chart</span>
                  </div>

                  <div className="space-y-2">
                    <div>
                      <label className="block text-[10px] text-slate-600 mb-0.5 font-semibold uppercase tracking-wider">1. Choose Client</label>
                      <select
                        value={targetClientId}
                        onChange={(e) => setTargetClientId(e.target.value)}
                        className="w-full bg-white border border-slate-300 focus:border-slate-500 rounded-lg px-2.5 py-1 text-xs text-slate-900 focus:outline-none transition-colors"
                      >
                        <option value="">Select client chart to link...</option>
                        {clients.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.last_name}, {c.first_name} (ID: {c.id} • {c.primary_payer_name})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-[10px] text-slate-600 mb-0.5 font-semibold uppercase tracking-wider">2. Document Classification</label>
                      <select
                        value={docType}
                        onChange={(e) => {
                          const next = e.target.value;
                          setDocType(next);
                          if (next === 'FEEDING_POC') {
                            const selectedCli = clients.find((c) => c.id === targetClientId);
                            setPocEndDate(selectedCli?.feeding_poc_end_date || '');
                          }
                        }}
                        className="w-full bg-white border border-slate-300 focus:border-slate-500 rounded-lg px-2.5 py-1 text-xs text-slate-900 font-semibold focus:outline-none transition-colors"
                      >
                        <option value="PHYSICIAN_SIGNED_POC">Speech CPOC</option>
                        <option value="PHYSICIAN_SIGNED_FEEDING_POC">Feeding CPOC</option>
                        <option value="FEEDING_POC">Feeding Plan of Care</option>
                        <option value="EVAL">Evaluation</option>
                        <option value="POC">Plan of Care</option>
                        <option value="QPR">Quarterly Progress Report (QPR)</option>
                        <option value="EXTERNAL">External Clinical Document</option>
                        <option value="OTHER">Other Clinical Document</option>
                      </select>
                    </div>

                    {(docType === 'PHYSICIAN_SIGNED_POC' || docType === 'POC' || docType === 'FEEDING_POC') && (
                      <div>
                        <label className="block text-[10px] text-slate-600 mb-0.5 font-semibold uppercase tracking-wider">
                          3. Verify / Update POC Expiration Date (Optional)
                        </label>
                        <input
                          type="date"
                          value={pocEndDate}
                          onChange={(e) => setPocEndDate(e.target.value)}
                          className="w-full bg-white border border-slate-300 focus:border-slate-500 rounded-lg px-2.5 py-1 text-xs text-slate-900 focus:outline-none font-mono transition-colors"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-[10px] text-slate-600 mb-0.5 font-semibold uppercase tracking-wider">Annotation / Notes</label>
                      <input
                        type="text"
                        value={attachNotes}
                        onChange={(e) => setAttachNotes(e.target.value)}
                        placeholder="Annotation (e.g. Signed Plan of Care returned from ordering physician)"
                        className="w-full bg-white border border-slate-300 focus:border-slate-500 rounded-lg px-2.5 py-1 text-xs text-slate-900 placeholder-slate-400 focus:outline-none transition-colors"
                      />
                    </div>

                    {attachError && (
                      <div className="p-2 bg-rose-50 border border-rose-200 text-rose-800 text-xs rounded">
                        {attachError}
                      </div>
                    )}

                    {attachSuccess && (
                      <div className="p-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-xs rounded flex items-center space-x-1.5">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        <span>Fax attached! Document now visible in client's Clinical Docs and Universal Email.</span>
                      </div>
                    )}

                    <button
                      onClick={handleAttachFax}
                      disabled={!targetClientId}
                      className="w-full py-2 px-3 rounded-lg text-xs font-semibold text-white flex items-center justify-center space-x-1.5 bg-slate-900 hover:bg-slate-800 disabled:opacity-40 disabled:pointer-events-none border border-slate-800 shadow-xs transition-colors cursor-pointer mt-1"
                    >
                      <Link2 className="w-3.5 h-3.5 text-slate-200" />
                      <span>Attach to Client Chart</span>
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="p-12 text-center text-slate-500 font-mono text-xs">
                Select a facsimile from the queue to view details, inspect transmission logs, or link to a client chart.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
